/**
 * Export exposure log for compliance reports
 * CSV and PDF generation for crew/aviation compliance
 */

import type { ExposureLogEntry } from '../types';
import {
  db,
  COLLECTIONS,
  isFirebaseAvailable,
  auth,
} from '../config/firebase';

/** ICRP recommended annual limit for occupational exposure (mSv) */
export const ICRP_ANNUAL_LIMIT_MSV = 6;

/**
 * Generate CSV string from log entries (includes crew metadata)
 */
export function generateLogCsv(entries: ExposureLogEntry[]): string {
  const header =
    'Timestamp,Route,Dose (µSv),Dose (mSv),Dose Rate (µSv/h),Flight ID,Crew Role\n';
  const rows = entries
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map(
      (e) =>
        `${e.timestamp},${e.route},${e.doseMicroSv},${(e.doseMicroSv / 1000).toFixed(2)},${e.doseRateMicroSvPerHour ?? ''},${e.flightId ?? ''},${e.crewRole ?? ''}`
    )
    .join('\n');
  return header + rows;
}

/**
 * Generate simple text-based PDF content (for native: use react-native-print or similar)
 * Returns HTML that can be printed/saved as PDF
 */
export function generateLogPdfHtml(
  entries: ExposureLogEntry[],
  annualTotalMicroSv: number
): string {
  const date = new Date().toISOString().slice(0, 10);
  const annualMsv = (annualTotalMicroSv / 1000).toFixed(2);
  const rows = entries
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map(
      (e) =>
        `<tr><td>${e.timestamp.slice(0, 19)}</td><td>${e.route}</td><td>${e.doseMicroSv}</td><td>${(e.doseMicroSv / 1000).toFixed(2)}</td><td>${e.doseRateMicroSvPerHour ?? '-'}</td><td>${e.flightId ?? '-'}</td><td>${e.crewRole ?? '-'}</td></tr>`
    )
    .join('');
  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>FVrad Exposure Report</title>
<style>body{font-family:sans-serif;padding:20px;color:#0f172a;background:#f8fafc}
table{border-collapse:collapse;width:100%;margin-top:16px}
th,td{border:1px solid #e2e8f0;padding:8px;text-align:left}
th{background:#e0f7fa;color:#00796b}
.warning{color:#b91c1c;font-weight:600}
</style></head>
<body>
<h1>FVrad Cosmic Radiation Exposure Report</h1>
<p>Generated: ${date} | Annual total: ${annualMsv} mSv | ICRP limit: ${ICRP_ANNUAL_LIMIT_MSV} mSv/year</p>
${annualTotalMicroSv >= ICRP_ANNUAL_LIMIT_MSV * 1_000_000 ? '<p class="warning">⚠ Approaching or exceeding ICRP annual limit</p>' : ''}
<table>
<tr><th>Timestamp</th><th>Route</th><th>Dose (µSv)</th><th>Dose (mSv)</th><th>Rate (µSv/h)</th><th>Flight ID</th><th>Crew Role</th></tr>
${rows}
</table>
<p style="margin-top:24px;font-size:12px;color:#64748b">FVrad - Cosmic radiation exposure tracking for aviation crew</p>
</body></html>`;
}

/**
 * Save log to Firestore for compliance backup (includes crew metadata)
 */
export async function saveLogToFirestore(
  entries: ExposureLogEntry[],
  annualTotalMicroSv: number
): Promise<boolean> {
  if (!isFirebaseAvailable || !db) return false;
  const uid = auth?.currentUser?.uid;
  if (!uid) return false;
  try {
    const report = {
      userId: uid,
      generatedAt: new Date().toISOString(),
      annualTotalMicroSv,
      entryCount: entries.length,
      entries: entries.map(({ id, ...e }) => ({
        ...e,
        flightId: e.flightId ?? null,
        crewRole: e.crewRole ?? null,
      })),
    };
    const firestore = db as { collection: (n: string) => { add: (d: unknown) => Promise<unknown> } };
    await firestore.collection(COLLECTIONS.DOSE_LOGS).add(report);
    return true;
  } catch {
    return false;
  }
}
